/**
 * Producción: los borradores de la estación del silo y su bajada al payload
 * wire (docs/esquema.md §5.2, matriz v4). Mismo trato que `viaje.ts`: la UI
 * habla en KILOS y el wire en GRAMOS enteros, la conversión vive SOLO acá, y
 * los ids se generan UNA vez al crear el borrador —sobreviven a cierres,
 * reintentos y a quedarse sin señal—.
 *
 * Los registros de la estación (boceto validado el 7/9,
 * `../Perejil/public/poc/produccion.html`, con la vuelta de tuerca del 10/9):
 * abrir un silo, el cambio de turno —que pesa los silos abiertos y los deja
 * abiertos—, cerrarlo —que pesa y cierra—, descartarlo, la bajada de carro y
 * la parada. En el wire son los mismos comandos: cada peso es un
 * `parcial.crear`, la fila de RE-P-05 por silo y por turno.
 *
 * Acá vive también la PROYECCIÓN de los silos abiertos: el servidor manda los
 * suyos en la bajada de catálogos, pero lo que este teléfono abrió y todavía no
 * subió no está ahí, y lo que cerró sin subir todavía figura. La pantalla tiene
 * que mostrar lo que la operaria ve en la planta, no lo que el servidor sabe.
 */

import { instanteLocal, msDeInstanteLocal, turnoDe, type Turno } from './tiempo';
import { TOLERANCIA_RECIENTE_MS } from './reciente';

/**
 * Tope duro de la interfaz (Nicolás, 11/9): más de 200 kg no es un silo, se bloquea.
 * Agata (14/9) le puso el motivo: «no debería pesar más de 200 kg, porque no lo levanta
 * el ascensor».
 * El boceto solo avisaba a los 190 y dejaba pasar cualquier cosa; el servidor tiene
 * apenas una cota sanitaria (5 t), así que el bloqueo que le sirve a la planta es este.
 */
export const MAX_BRUTO_KG = 200;
/** Envasado acepta 170 kg con 20 de tolerancia (r. 48): entre 190 y el tope se avisa. */
export const AVISO_BRUTO_KG = 190;
/**
 * Tope duro de la tara (Nicolás, 12/9: «sabemos que son siempre los mismos, no debería
 * dejar avanzar»). Las taras reales van de 60 a 70 kg: en 2025, 944 de 958 aperturas
 * fueron 66 o 67 y ninguna real salió de 61–69; lo que quedó fuera eran dedazos
 * (6, 102, 119). El margen 50–80 los ataja sin bloquear una tarjeta rara.
 */
export const MIN_TARA_KG = 50;
export const MAX_TARA_KG = 80;
/**
 * Por encima de esto no es un dedazo, pero tampoco un silo vacío (las reales van de 60 a
 * 70): probablemente quedaron restos del silo anterior. La tara se tipea cada vez justamente
 * para eso (Agata, 14/9: «no se puede llenar sobre mercadería vieja»), así que se avisa.
 */
export const TARA_CON_RESTOS_KG = 70;
/** Una parada no supera un turno (CHECK del DDL). */
export const MAX_MINUTOS_PARADA = 480;
export const MIN_DIGITOS_PRECINTO = 5;
export const MAX_DIGITOS_PRECINTO = 7;

const G_POR_KG = 1000;

// ── Borradores ────────────────────────────────────────────────────────────

interface Comun {
	capture_id: string;
	mutacion_id: string;
	paso: number;
	/** Una sola persona en producción, pero se reusa la grilla de verde, que habla en listas. */
	personas: string[];
	actualizado_en: number;
}

/** `llenado.abrir`: la identidad del silo llenado nace acá, con su versión 1. */
export interface BorradorAbrir extends Comun {
	tipo: 'abrir';
	llenado_id: string;
	version_id: string;
	campania_id: string | null;
	silo_fisico_id: string | null;
	lote_campo_id: string | null;
	trieur_id: string | null;
	horno_id: string | null;
	precinto: string;
	tara_kg: number | null;
	abierto_ms: number | null;
	nota: string | null;
	/** La foto del precinto (§5.3): el blob está en `fotos` bajo `foto_id`; null = todavía no se sacó. */
	foto: { foto_id: string; capturada_en: string } | null;
	/** «Seguir sin foto»: la encargada lo eligió (la cámara no anda). Un borrador viejo no lo trae. */
	sin_foto?: boolean;
	/**
	 * El lote lo puso la propuesta del último carro, no la encargada (§12.16): si aparecen mezclas, o el
	 * catálogo resulta de otra jornada, se borra; lo que eligió ella (`false`, también al seguir con la
	 * propuesta marcada) se respeta. Un borrador de antes no lo trae, y con mezclas su lote se vuelve a pedir.
	 */
	lote_propuesto?: boolean;
	/** El lote elegido dejó de estar en la lista (§12.16): hasta que la encargada toque otro, no se propone nada. */
	elegir_de_nuevo?: boolean;
	/** «Otras mezclas de la campaña» desplegado: las terminadas hace más de tres jornadas (§12.16). */
	ver_mezclas_terminadas?: boolean;
}

/**
 * Lo que hace falta para pesar un silo: contra qué se compara y desde cuándo
 * corre el parcial. El `inicio_en` NO se pregunta: es el fin del parcial anterior
 * o la apertura del silo, y se congela al tomar la foto — un refresco de
 * catálogos no puede moverlo bajo los pies de quien está cargando.
 */
export interface Pesada {
	/** Cuándo se abrió el silo: con el «desde», dice cómo viene (abierto 06:10 · última pesada 14:20). */
	abierto_en: string | null;
	inicio_en: string | null;
	/** Snapshot del bruto anterior (o la tara base): con esto se valida sin volver al catálogo. */
	anterior_g: number | null;
	tara_base_g: number | null;
	bruto_kg: number | null;
}

/** Un silo del cambio de turno: su foto, su peso y el `parcial.crear` propio que va a viajar. */
export interface PesadaDeSilo extends SiloVisto, Pesada {
	parcial_id: string;
	/** Una mutación por silo: dos silos abiertos son dos parciales en la cola. */
	mutacion_id: string;
}

/**
 * El cambio de turno (10/9): la saliente pesa los silos que quedan abiertos —uno
 * o dos, nunca más— y la entrante los recibe. Un `parcial.crear` por silo, con
 * el mismo turno, la misma hora de fin y la misma pareja de personas.
 */
export interface BorradorTurno extends Comun {
	tipo: 'turno';
	/** Los abiertos al arrancar, con su foto congelada. */
	silos: PesadaDeSilo[];
	turno: Turno | null;
	hasta_ms: number | null;
	recibe_id: string | null;
	nota: string | null;
	/**
	 * v6 (§12.2): «¿Algo para la que entra?», opcional. Si tiene texto, la confirmación encola un
	 * `aviso.crear` junto con las pesadas. Opcionales en el tipo: un borrador guardado antes no los trae.
	 */
	aviso_texto?: string | null;
	aviso_id?: string;
	aviso_mutacion_id?: string;
}

/**
 * La foto del silo que se tomó al elegirlo. Todo lo que el payload y la pantalla
 * necesitan viaja acá dentro: si otro teléfono entrega un turno mientras esta
 * carga está a medias, la revisión sigue mostrando lo que la operaria eligió y no
 * un dato nuevo que nunca miró (Codex, ronda del diff, 8/9).
 */
interface SiloVisto {
	llenado_id: string | null;
	/** Para la pantalla: el número del silo y su precinto, congelados. */
	silo_numero: string | null;
	precinto: string | null;
	/** El neto conocido al elegirlo; `null` si no tenía ningún turno pesado. */
	neto_visto_g: number | null;
	/** Silo físico, trieur y apertura del silo elegido: viajan en el resumen del cierre (2/10). Un borrador viejo no lo trae. */
	intervalo?: IntervaloEnResumen | null;
	/**
	 * La mutación de la que depende esto para poder viajar: el último parcial si
	 * sigue en la cola, o la apertura si el silo se abrió acá y no subió todavía.
	 */
	padre_mutacion_id: string | null;
}

/** Lo común de las transiciones del silo: la versión vigente que se vio es la base del CAS. */
interface Transicion extends Comun, SiloVisto {
	version_id: string;
	version_base_id: string | null;
}

/**
 * Cerrar pesa y cierra (10/9): el peso final es el último parcial del silo. Si el
 * bruto supera al último pesado viaja un `parcial.crear` y detrás el cierre, que
 * lo lleva de testigo; si es el mismo —se cierra recién pesado, o vacío— no hay
 * nada nuevo que registrar y viaja solo el cierre.
 */
export interface BorradorCerrar extends Transicion, Pesada {
	tipo: 'cerrar';
	/** Se entró tocando la tarjeta del silo en el inicio: el paso de elegirlo se saltea. */
	desde_inicio: boolean;
	parcial_id: string;
	parcial_mutacion_id: string;
	cerrado_ms: number | null;
	/** El último parcial vigente que la operaria vio; el servidor falla si ya no lo es. */
	ultimo_parcial_id: string | null;
	/** v6 (§12.4): «¿Alguna nota?» al cerrar, opcional; viaja como `nota_llenado.crear` aparte. */
	nota_texto?: string | null;
	nota_id?: string;
	nota_mutacion_id?: string;
}

export interface BorradorDescartar extends Transicion {
	tipo: 'descartar';
	cerrado_ms: number | null;
	motivo: string | null;
}

export interface BorradorCarro extends Comun {
	tipo: 'carro';
	cambio_carro_id: string;
	campania_id: string | null;
	carro_id: string | null;
	registrado_ms: number | null;
	nota: string | null;
}

export interface BorradorParada extends Comun {
	tipo: 'parada';
	parada_id: string;
	campania_id: string | null;
	motivo_parada_id: string | null;
	minutos: number | null;
	turno: Turno | null;
	/**
	 * El ARRANQUE del turno informado, congelado al elegirlo: `parada.registrado_en`
	 * no es la hora del tipeo. Persistido, para que retomar el borrador al día
	 * siguiente no lo mueva de jornada.
	 */
	registrado_ms: number | null;
	/** «Qué pasó»: el único texto libre del teléfono (Agata, 7/9). No vinculante. */
	nota: string | null;
}

/** v6 (§12.2): un aviso para las demás encargadas. La hora es la de la confirmación: no se pregunta. */
export interface BorradorAviso extends Comun {
	tipo: 'aviso';
	aviso_id: string;
	campania_id: string | null;
	texto: string | null;
}

/** v6 (§12.4): una nota a un silo abierto. El silo se congela al elegirlo, como en las transiciones. */
export interface BorradorNota extends Comun {
	tipo: 'nota';
	nota_id: string;
	llenado_id: string | null;
	silo: string | null;
	precinto_del_silo: string | null;
	/** La apertura del silo, si sigue en la cola de este teléfono: la nota la espera. */
	padre_mutacion_id: string | null;
	texto: string | null;
}

export type BorradorProduccion = BorradorAbrir | BorradorTurno | BorradorCerrar | BorradorDescartar | BorradorCarro | BorradorParada | BorradorAviso | BorradorNota;

/** El texto de un aviso o una nota, como lo valida el servidor: sin espacios al borde, de 1 a 400. */
export const MAX_TEXTO_AVISO = 400;
export function textoValido(texto: string | null | undefined): string | null {
	const limpio = (texto ?? '').trim();
	return limpio.length >= 1 && [...limpio].length <= MAX_TEXTO_AVISO ? limpio : null;
}

export function armarPayloadAviso(avisoId: string, campaniaId: string, texto: string | null | undefined, personaId: string | undefined, registradoMs: number): Record<string, unknown> {
	return {
		aviso_id: avisoId,
		campania_id: campaniaId,
		texto: exigir(textoValido(texto), 'texto'),
		registrado_en: instanteLocal(registradoMs),
		persona_id: exigir(personaId, 'quién'),
	};
}

export function armarPayloadNota(notaId: string, llenadoId: string | null, texto: string | null | undefined, personaId: string | undefined, registradoMs: number): Record<string, unknown> {
	return {
		nota_id: notaId,
		llenado_id: exigir(llenadoId, 'silo'),
		texto: exigir(textoValido(texto), 'texto'),
		registrado_en: instanteLocal(registradoMs),
		persona_id: exigir(personaId, 'quién'),
	};
}

// ── Payloads (docs/esquema.md §5.2, matriz v4) ────────────────────────────

export class BorradorIncompleto extends Error {}

function exigir<T>(valor: T | null | undefined, campo: string): T {
	if (valor === null || valor === undefined) throw new BorradorIncompleto(`falta ${campo}`);
	return valor;
}

/**
 * Un peso que la pantalla no dejaría pasar tampoco pasa por acá: un borrador
 * guardado antes del tope de 200 kg podría llegar a la revisión sin volver a
 * validarse (Codex, 11/9).
 */
function exigirPesoKg(kg: number | null, campo: string): number {
	const valor = exigir(kg, campo);
	if (valor <= 0 || valor > MAX_BRUTO_KG) throw new BorradorIncompleto(`${campo} fuera de rango`);
	return valor;
}

/** Kilos de pantalla → gramos enteros de wire. */
export function aGramos(kg: number): number {
	return Math.round(kg * G_POR_KG);
}

export function armarPayloadAbrir(b: BorradorAbrir, campaniaId: string): Record<string, unknown> {
	return {
		llenado_id: b.llenado_id,
		version_id: b.version_id,
		campania_id: campaniaId,
		silo_fisico_id: exigir(b.silo_fisico_id, 'silo'),
		horno_id: exigir(b.horno_id, 'horno'),
		trieur_id: exigir(b.trieur_id, 'trieur'),
		lote_campo_id: exigir(b.lote_campo_id, 'lote'),
		precinto: b.precinto,
		abierto_en: instanteLocal(exigir(b.abierto_ms, 'hora de apertura')),
		tara_base_g: aGramos(exigirPesoKg(b.tara_kg, 'tara')),
		persona_id: exigir(b.personas[0], 'quién'),
		notas: b.nota?.trim() ? b.nota.trim() : null,
	};
}

/** El contexto que comparten las pesadas de un mismo cambio de turno (o el del cierre). */
export interface ContextoDePesada {
	turno: Turno | null;
	fin_ms: number | null;
	entregada_por: string | undefined;
	recibida_por: string | null;
	nota: string | null;
}

export function armarPayloadParcial(p: Pick<PesadaDeSilo, 'parcial_id' | 'llenado_id'> & Pesada, c: ContextoDePesada): Record<string, unknown> {
	return {
		parcial_id: p.parcial_id,
		llenado_id: exigir(p.llenado_id, 'silo'),
		turno: exigir(c.turno, 'turno'),
		inicio_en: exigir(p.inicio_en, 'inicio'),
		fin_en: instanteLocal(exigir(c.fin_ms, 'hora de fin')),
		bruto_g: aGramos(exigirPesoKg(p.bruto_kg, 'bruto')),
		entregada_por: exigir(c.entregada_por, 'quién entrega'),
		recibida_por: c.recibida_por,
		notas: c.nota?.trim() ? c.nota.trim() : null,
	};
}

/**
 * El parcial que el cierre deja detrás. Su turno es el del ARRANQUE del parcial —el
 * turno que produjo ese material—, no el del reloj al cerrar: un silo cerrado a las
 * 06:03 con la última pesada a las 22:00 es producción de la noche (Codex, 11/9).
 * Nadie recibe: no hay entrega.
 */
export function armarPayloadParcialDelCierre(b: BorradorCerrar): Record<string, unknown> {
	const cerradoMs = exigir(b.cerrado_ms, 'hora de cierre');
	const turno = turnoDe(msDeInstanteLocal(exigir(b.inicio_en, 'inicio')));
	return armarPayloadParcial(b, { turno, fin_ms: cerradoMs, entregada_por: b.personas[0], recibida_por: null, nota: null });
}

/**
 * ¿Esta pesada deja un parcial? Solo si el bruto supera al último pesado (o a la
 * tara): el servidor exige la cadena estrictamente creciente, y un silo que no
 * cambió no tiene turno que registrar —ni al cerrarlo ni en el cambio de turno—.
 */
export function dejaParcial(p: Pick<Pesada, 'bruto_kg' | 'anterior_g'>): boolean {
	return p.bruto_kg !== null && aGramos(p.bruto_kg) > (p.anterior_g ?? 0);
}

export function armarPayloadCerrar(b: BorradorCerrar): Record<string, unknown> {
	return {
		llenado_id: exigir(b.llenado_id, 'silo'),
		version_id: b.version_id,
		version_base_id: exigir(b.version_base_id, 'versión del silo'),
		cerrado_en: instanteLocal(exigir(b.cerrado_ms, 'hora de cierre')),
		// El testigo es el último parcial que va a existir: el que este cierre deja, o el visto.
		ultimo_parcial_id: dejaParcial(b) ? b.parcial_id : b.ultimo_parcial_id,
		persona_id: exigir(b.personas[0], 'quién'),
	};
}

export function armarPayloadDescartar(b: BorradorDescartar): Record<string, unknown> {
	return {
		llenado_id: exigir(b.llenado_id, 'silo'),
		version_id: b.version_id,
		version_base_id: exigir(b.version_base_id, 'versión del silo'),
		cerrado_en: b.cerrado_ms === null ? null : instanteLocal(b.cerrado_ms),
		motivo_descarte: exigir(b.motivo, 'motivo'),
		persona_id: exigir(b.personas[0], 'quién'),
	};
}

export function armarPayloadCarro(b: BorradorCarro, campaniaId: string): Record<string, unknown> {
	return {
		cambio_carro_id: b.cambio_carro_id,
		campania_id: campaniaId,
		carro_id: exigir(b.carro_id, 'carro'),
		viaje_raiz_id: null,
		registrado_en: instanteLocal(exigir(b.registrado_ms, 'hora')),
		persona_id: exigir(b.personas[0], 'quién'),
		notas: b.nota?.trim() ? b.nota.trim() : null,
	};
}

/** Una fila del catálogo `carros_ultimo_viaje` (§12.1); `entrada_en` y `bajado` llegaron el 6/10. */
export interface CarroUltimoViaje {
	carro_id: string;
	nro_viaje: number | null;
	lote_campo_id: string | null;
	ambiguo: number;
	entrada_en?: string;
	bajado?: number;
}

export interface ViajeDelCarro {
	nro_viaje: number | null;
	lote_campo_id: string | null;
	/** Dos viajes a la misma hora: no se sabe de cuál viene. */
	ambiguo: boolean;
	/** Ya figura bajado desde esa llegada: en el servidor o en este teléfono. */
	bajado: boolean;
}

/**
 * De qué viaje viene el carro y si ya figura bajado desde esa llegada (§12.1). Agata, 6/10: «ya
 * bajaron el 150 y 151, pero les siguen apareciendo para bajar» — el catálogo decía el último viaje
 * y la pantalla lo ponía debajo del carro aunque ya hubiera bajado. Ahora el servidor dice si conoce
 * una bajada en o después de la llegada, y una bajada de este teléfono que el catálogo todavía no
 * refleja, fechada en o después de ella, también lo baja. Sin viaje conocido, null: no se adivina.
 * Avisa, no bloquea: un carro que figura bajado casi siempre volvió en un viaje que verde no subió.
 */
export function viajeDelCarro(carroId: string, ultimos: CarroUltimoViaje[], bajadasLocales: Array<{ tipo: string; payload: unknown }>): ViajeDelCarro | null {
	const fila = ultimos.find((f) => f.carro_id === carroId);
	if (!fila) return null;
	const llegada = fila.entrada_en;
	const bajadoAca =
		llegada !== undefined &&
		bajadasLocales.some((m) => m.tipo === 'cambio_carro.crear' && campo(m.payload, 'carro_id') === carroId && (campo(m.payload, 'registrado_en') ?? '') >= llegada);
	return { nro_viaje: fila.nro_viaje, lote_campo_id: fila.lote_campo_id, ambiguo: fila.ambiguo === 1, bajado: fila.bajado === 1 || bajadoAca };
}

/**
 * `[DECIDIDO 6/10/2026]` Nicolás: en «¿Qué carro bajó?» solo se eligen los carros que figuran traídos
 * y sin bajar; los demás se ven apagados con el motivo. Un carro que verde todavía no subió no se
 * puede elegir hasta que ese teléfono tenga señal. El servidor no rechaza nada: offline una bajada
 * puede llegar después de otra del mismo carro y sigue siendo un dato.
 */
export function carroElegible(viaje: ViajeDelCarro | null): boolean {
	return viaje !== null && !viaje.bajado;
}

/**
 * El catálogo `ultima_bajada` de producción (§5.5, §12.1; issue #82): la bajada vigente más reciente de
 * la planta, de cualquier teléfono, con el viaje y el lote de ESA bajada.
 */
export interface UltimaBajada {
	/** La raíz de la bajada: la identidad que manda el teléfono en `cambio_carro.crear`. */
	cambio_carro_id: string;
	carro_id: string;
	registrado_en: string;
	nro_viaje: number | null;
	lote_campo_id: string | null;
	/** 1: no se sabe de qué lote viene (su viaje es un empate, o hay otra en el mismo minuto de otro lote). */
	ambiguo: number;
}

/**
 * El techo de la última bajada para una hora del servidor (§5.5): esa hora más la tolerancia de reloj de
 * `verde_reciente`, en hora de la planta. Una bajada fechada más allá —un reloj adelantado, o una hora
 * corrida de más con «+ 15 min», que no tiene tope— no tapa a las reales. Lo usan el servidor, con su
 * hora, y el teléfono, con la del snapshot (`catalogos_hora_servidor`), nunca con su reloj.
 */
export function techoDeLaUltimaBajada(horaDelServidorMs: number): string {
	return instanteLocal(horaDelServidorMs + TOLERANCIA_RECIENTE_MS);
}

/**
 * La más reciente de varias bajadas por `registrado_en` (§12.1, issue #82). Con una sola en el minuto
 * mayor, esa; con varias, la primera si todas vienen del mismo lote conocido, y si no, la misma sin
 * viaje ni lote y ambigua: dos bajadas en el mismo minuto de lotes distintos no eligen, como dos viajes
 * a la misma hora. Una repetida por `cambio_carro_id` cuenta una vez, la primera. La usan el servidor
 * (el catálogo) y el teléfono (el catálogo más lo propio), para que decidan con la misma regla.
 */
export function bajadaMasReciente(bajadas: UltimaBajada[]): UltimaBajada | null {
	const unicas = bajadas.filter((b, i) => bajadas.findIndex((o) => o.cambio_carro_id === b.cambio_carro_id) === i);
	const mayor = unicas.reduce<string | null>((max, b) => (max === null || b.registrado_en > max ? b.registrado_en : max), null);
	const delMinuto = unicas.filter((b) => b.registrado_en === mayor);
	const [primera] = delMinuto;
	if (primera === undefined) return null;
	if (delMinuto.length === 1) return primera;
	const lotes = new Set(delMinuto.map((b) => (b.ambiguo === 1 ? null : b.lote_campo_id)));
	return lotes.size === 1 && !lotes.has(null) ? primera : { ...primera, nro_viaje: null, lote_campo_id: null, ambiguo: 1 };
}

/** Lo que «¿De qué lote es?» propone al abrir un silo: el lote, de qué carro y viaje, y cuándo bajó. */
export interface LoteDelUltimoCarro {
	lote_campo_id: string;
	carro_id: string;
	nro_viaje: number | null;
	registrado_en: string;
}

/**
 * El lote del último carro bajado en la planta, para proponerlo al abrir un silo (§12.1, issue #82).
 * Agata, 7/10: «…a veces no se acuerdan de qué origen es el carro y ya no tienen planilla donde
 * consultarlo»; Nicolás, 7/10: se propone marcado, no se precontesta. Es la más reciente por
 * `registrado_en` declarado entre la del catálogo —de cualquier teléfono— y las de este teléfono que el
 * catálogo todavía no refleja (cola y recibos: las mismas de «ya bajó»). Una propia toma el lote de su
 * carro en `carros_ultimo_viaje` solo si esa llegada es anterior o igual a la bajada y no es un empate:
 * si el carro volvió a entrar después, el teléfono no conoce el viaje de antes. Sin lote conocido, o con
 * dos del minuto mayor que no coinciden, null: no se inventa. Un catálogo de antes del cambio no trae
 * `ultima_bajada`, y entonces no se propone nada.
 *
 * Las propias tienen el mismo techo que el servidor, sacado de la hora del snapshot (`horaDelCatalogo`,
 * la `hora_servidor` con que se bajó): una fechada después no se propone, y tampoco otra en su lugar,
 * porque el teléfono no distingue una hora corrida de más (el reloj de la pantalla no tiene tope) de un
 * catálogo viejo sin señal, y en ese caso la anterior del catálogo sería el lote equivocado (Codex,
 * ronda del diff, 8/10). Sin la hora del snapshot no se puede acotar: no se propone.
 */
export function loteDelUltimoCarro(
	catalogos: { ultima_bajada?: UltimaBajada | null; carros_ultimo_viaje?: CarroUltimoViaje[] },
	bajadasLocales: Array<{ tipo: string; payload: unknown }>,
	horaDelCatalogo: string | undefined,
): LoteDelUltimoCarro | null {
	if (catalogos.ultima_bajada === undefined) return null;
	const horaMs = horaDelCatalogo === undefined ? Number.NaN : Date.parse(horaDelCatalogo);
	if (!Number.isFinite(horaMs)) return null;
	const techo = techoDeLaUltimaBajada(horaMs);
	const ultimos = catalogos.carros_ultimo_viaje ?? [];
	const propias: UltimaBajada[] = [];
	for (const m of bajadasLocales) {
		if (m.tipo !== 'cambio_carro.crear') continue;
		const id = campo(m.payload, 'cambio_carro_id');
		const carroId = campo(m.payload, 'carro_id');
		const registrado = campo(m.payload, 'registrado_en');
		if (id === null || carroId === null || registrado === null) continue;
		const fila = ultimos.find((f) => f.carro_id === carroId);
		// El viaje de la bajada es el último del carro solo si llegó antes de bajarlo (`entrada_en <= registrado_en`, §12.1).
		const deAntes = fila !== undefined && fila.entrada_en !== undefined && fila.entrada_en <= registrado ? fila : null;
		propias.push({
			cambio_carro_id: id,
			carro_id: carroId,
			registrado_en: registrado,
			nro_viaje: deAntes?.nro_viaje ?? null,
			lote_campo_id: deAntes?.lote_campo_id ?? null,
			ambiguo: deAntes?.ambiguo === 1 ? 1 : 0,
		});
	}
	// La del catálogo primero: si es la misma bajada que una propia, gana la verdad del servidor.
	const ultima = bajadaMasReciente(catalogos.ultima_bajada === null ? propias : [catalogos.ultima_bajada, ...propias]);
	if (ultima === null || ultima.registrado_en > techo || ultima.ambiguo === 1 || ultima.lote_campo_id === null) return null;
	return { lote_campo_id: ultima.lote_campo_id, carro_id: ultima.carro_id, nro_viaje: ultima.nro_viaje, registrado_en: ultima.registrado_en };
}

export function armarPayloadParada(b: BorradorParada, campaniaId: string): Record<string, unknown> {
	return {
		parada_id: b.parada_id,
		campania_id: campaniaId,
		motivo_parada_id: exigir(b.motivo_parada_id, 'motivo'),
		minutos: exigir(b.minutos, 'minutos'),
		turno: exigir(b.turno, 'turno'),
		registrado_en: instanteLocal(exigir(b.registrado_ms, 'hora')),
		persona_id: exigir(b.personas[0], 'quién'),
		notas: b.nota?.trim() ? b.nota.trim() : null,
	};
}

// ── Validaciones de pantalla ──────────────────────────────────────────────

/** El número grabado en el sello: de 5 a 7 dígitos, sin ceros a la izquierda perdidos. */
export function precintoCompleto(precinto: string): boolean {
	return new RegExp(`^[0-9]{${MIN_DIGITOS_PRECINTO},${MAX_DIGITOS_PRECINTO}}$`).test(precinto);
}

/** Bloqueo, no aviso: fuera de 50–80 kg no es una tara, es un dedazo (el POC solo avisaba). */
export function taraAceptable(kg: number | null): boolean {
	return kg !== null && kg >= MIN_TARA_KG && kg <= MAX_TARA_KG;
}

/** Aviso, no bloqueo: una tara aceptable pero por encima de lo normal huele a restos del silo anterior. */
export function taraConRestos(kg: number | null): boolean {
	return kg !== null && taraAceptable(kg) && kg > TARA_CON_RESTOS_KG;
}

export type VeredictoPesada = { ok: false; motivo: 'menor' | 'excesivo' } | { ok: true; pesa: boolean; aviso: 'muy_pesado' | null };

/**
 * Un silo no se cierra vacío (Agata, 14/9: «no se deja… genera ruido de gusto»): cerrar con
 * el silo nunca pesado y la balanza en la tara es bloqueo, no aviso. El que se abrió por error
 * lo anula la oficina.
 */
export function cierraVacio(cierre: boolean, brutoKg: number | null, anteriorG: number, taraG: number): boolean {
	return cierre && anteriorG <= taraG && brutoKg !== null && aGramos(brutoKg) <= taraG;
}

/**
 * La regla del peso, la misma en el cambio de turno y en el cierre. Menos que el
 * último pesado (o que la tara) es imposible: la balanza no miente hacia abajo. El
 * MISMO peso vale y no deja parcial (`pesa: false`): el silo no recibió material en
 * el turno, o se cierra recién pesado, o vacío. Con dos silos abiertos, exigir que
 * los dos aumenten trababa el cambio de turno entero (Codex, 11/9). Y más del tope
 * no pasa: avisar y dejar seguir con 888 kg no tenía sentido (11/9).
 */
export function evaluarPesada(brutoKg: number | null, anteriorG: number): VeredictoPesada {
	if (brutoKg === null || aGramos(brutoKg) < anteriorG) return { ok: false, motivo: 'menor' };
	if (brutoKg > MAX_BRUTO_KG) return { ok: false, motivo: 'excesivo' };
	return { ok: true, pesa: aGramos(brutoKg) > anteriorG, aviso: brutoKg > AVISO_BRUTO_KG ? 'muy_pesado' : null };
}

export function minutosValidos(minutos: number | null): boolean {
	return minutos !== null && Number.isInteger(minutos) && minutos >= 1 && minutos <= MAX_MINUTOS_PARADA;
}

/**
 * El wire tiene resolución de MINUTO: dos instantes distintos en milisegundos
 * pueden serializarse iguales y el servidor rechaza `fin_en <= inicio_en`. Se
 * compara como texto, que es lo que va a viajar (Codex, ronda del plan, 8/9).
 */
export function finPosteriorAlInicio(inicioEn: string | null, finMs: number | null): boolean {
	return inicioEn !== null && finMs !== null && instanteLocal(finMs) > inicioEn;
}

/**
 * Descartar un silo abierto exige `cerrado_en`, y toda versión con cierre exige
 * que no sea anterior al último turno entregado (§5.2.9(b)). Se atajan acá para
 * no mandar a la planta un rechazo que se ve recién al sincronizar.
 */
export function cierrePosteriorAlUltimoParcial(cerradoMs: number | null, ultimoFinEn: string | null): boolean {
	if (cerradoMs === null) return false;
	return ultimoFinEn === null || instanteLocal(cerradoMs) >= ultimoFinEn;
}

/** El servidor exige cierre ESTRICTAMENTE posterior a la apertura, aunque no haya parciales. */
export function cierrePosteriorALaApertura(cerradoMs: number | null, abiertoEn: string | null): boolean {
	if (cerradoMs === null) return false;
	return abiertoEn === null || instanteLocal(cerradoMs) > abiertoEn;
}

// ── Proyección de los silos abiertos ──────────────────────────────────────

/** Lo que el servidor manda en la bajada (§5.5, `llenados_abiertos`). */
export interface LlenadoAbiertoServidor {
	llenado_id: string;
	version_id: string;
	silo_fisico_id: string;
	horno_id: string;
	trieur_id: string;
	lote_campo_id: string;
	precinto: string;
	abierto_en: string;
	tara_base_g: number;
	ultimo_parcial_id: string | null;
	ultimo_bruto_g: number | null;
	ultimo_fin_en: string | null;
}

/** Una fila de la cola local, con lo poco que la proyección necesita. */
export interface MutacionPendiente {
	tipo: string;
	payload: unknown;
	/**
	 * Id de la mutación en la cola, si TODAVÍA está pendiente. Es lo que se usa como
	 * `padre_id` de lo que dependa de este efecto: la identidad del REGISTRO no sirve
	 * —el silo la comparte con su cierre, y el cierre terminaba esperándose a sí
	 * mismo (Codex, ronda del diff, 8/9)—.
	 */
	mutacion_id?: string;
	/** v5: de un recibo, la identidad del registro (la proyección de embolsado dedupe por raíz). */
	raiz_id?: string;
	/** v5: de un recibo de `pallet.abrir`, el número que asignó el servidor. */
	numero?: number;
	/** v6: de un recibo de muestra, las versiones que pisó (ver `Recibo.cadena`). */
	cadena?: { bases: string[]; letra: string | null };
	/** §12.17: el resumen de la fila o del recibo (la tarjeta de la humedad viaja ahí). */
	resumen?: unknown;
	/** §12.17: de un recibo de muestra, el número que asignó el servidor. */
	nro_muestra?: number | null;
}

export interface SiloAbierto extends LlenadoAbiertoServidor {
	/** Cuántas entregas de turno lleva: el boceto lo muestra en la tarjeta. */
	turnos: number;
	/** Abierto en este teléfono y todavía sin acuse: existe para la operaria, no para el servidor. */
	local: boolean;
	/** La mutación que lo abrió, mientras siga en la cola: de ella dependen sus hijos. */
	abierto_mutacion_id: string | null;
	/** La mutación del último turno entregado, mientras siga en la cola. */
	ultimo_parcial_mutacion_id: string | null;
}

function campo(payload: unknown, clave: string): string | null {
	if (typeof payload !== 'object' || payload === null) return null;
	const valor = (payload as Record<string, unknown>)[clave];
	return typeof valor === 'string' ? valor : null;
}

function numero(payload: unknown, clave: string): number | null {
	if (typeof payload !== 'object' || payload === null) return null;
	const valor = (payload as Record<string, unknown>)[clave];
	return typeof valor === 'number' ? valor : null;
}

/**
 * Lo que la operaria tiene delante = lo que el servidor sabe + lo que este
 * teléfono hizo y todavía no subió. Las mutaciones se aplican EN ORDEN de cola
 * (el llamador las pasa ordenadas por seq), que es el mismo orden en que el
 * servidor las va a aplicar: la proyección se parece a lo que va a quedar.
 *
 * Un silo que este teléfono cerró desaparece aunque el catálogo viejo lo siga
 * trayendo; uno que abrió aparece aunque el servidor no lo conozca. Cuando el
 * ACK llega y el catálogo se refresca, la fila sale de la cola y entra por la
 * otra puerta: la tarjeta no parpadea porque las dos fuentes se fusionan por
 * `llenado_id`.
 */
/**
 * Qué hay que aplicarle al catálogo para que la pantalla diga la verdad:
 *
 * 1. Los efectos ya CONFIRMADOS por el servidor cuyo acuse es POSTERIOR al snapshot
 *    de catálogos que está guardado. Sin esto, un silo desaparece de la pantalla en
 *    cuanto llega su propio ACK —la fila sale de la cola y el catálogo viejo no lo
 *    tiene—. Y comparar contra la hora del servidor y no contra el reloj local es
 *    lo que evita el error inverso: un efecto que el catálogo YA refleja no se
 *    vuelve a aplicar encima, así que un parcial de otro teléfono no retrocede y un
 *    silo que gestión cerró no resucita (Codex, ronda del plan, 8/9).
 * 2. Los pendientes, que el servidor todavía no vio.
 *
 * Queda una ventana de milisegundos: el snapshot pudo armarse en el servidor justo
 * antes de un acuse y llegar después. Se cierra sola en el refresco siguiente, que
 * el sync fuerza apenas entra algo de producción.
 */
export function mutacionesAProyectar(
	confirmados: Array<{ tipo?: string; payload?: unknown; sincronizado_en: string; raiz_id?: string; numero?: number; cadena?: { bases: string[]; letra: string | null }; resumen?: unknown; nro_muestra?: number | null }>,
	pendientes: MutacionPendiente[],
	horaDelCatalogo: string | undefined,
): MutacionPendiente[] {
	// Sin `mutacion_id`: ya tienen acuse, así que nada los espera.
	const sinReflejar = confirmados
		.filter((r) => r.tipo !== undefined && (horaDelCatalogo === undefined || r.sincronizado_en > horaDelCatalogo))
		.sort((a, b) => a.sincronizado_en.localeCompare(b.sincronizado_en))
		.map((r) => ({
			tipo: r.tipo as string,
			payload: r.payload,
			...(r.raiz_id === undefined ? {} : { raiz_id: r.raiz_id }),
			...(r.numero === undefined ? {} : { numero: r.numero }),
			...(r.cadena === undefined ? {} : { cadena: r.cadena }),
			...(r.resumen === undefined ? {} : { resumen: r.resumen }),
			...(r.nro_muestra === undefined || r.nro_muestra === null ? {} : { nro_muestra: r.nro_muestra }),
		}));
	return [...sinReflejar, ...pendientes];
}

export function proyectarSilosAbiertos(delServidor: LlenadoAbiertoServidor[], pendientes: MutacionPendiente[]): SiloAbierto[] {
	const abiertos = new Map<string, SiloAbierto>();
	for (const l of delServidor) {
		abiertos.set(l.llenado_id, { ...l, turnos: 0, local: false, abierto_mutacion_id: null, ultimo_parcial_mutacion_id: null });
	}

	for (const m of pendientes) {
		const llenadoId = campo(m.payload, 'llenado_id');
		if (llenadoId === null) continue;
		if (m.tipo === 'llenado.abrir') {
			abiertos.set(llenadoId, {
				llenado_id: llenadoId,
				version_id: campo(m.payload, 'version_id') ?? '',
				silo_fisico_id: campo(m.payload, 'silo_fisico_id') ?? '',
				horno_id: campo(m.payload, 'horno_id') ?? '',
				trieur_id: campo(m.payload, 'trieur_id') ?? '',
				lote_campo_id: campo(m.payload, 'lote_campo_id') ?? '',
				precinto: campo(m.payload, 'precinto') ?? '',
				abierto_en: campo(m.payload, 'abierto_en') ?? '',
				tara_base_g: numero(m.payload, 'tara_base_g') ?? 0,
				ultimo_parcial_id: null,
				ultimo_bruto_g: null,
				ultimo_fin_en: null,
				turnos: 0,
				local: m.mutacion_id !== undefined,
				abierto_mutacion_id: m.mutacion_id ?? null,
				ultimo_parcial_mutacion_id: null,
			});
		} else if (m.tipo === 'llenado.cerrar' || m.tipo === 'llenado.descartar') {
			abiertos.delete(llenadoId);
		} else if (m.tipo === 'parcial.crear') {
			const silo = abiertos.get(llenadoId);
			if (!silo) continue;
			silo.ultimo_parcial_id = campo(m.payload, 'parcial_id');
			silo.ultimo_bruto_g = numero(m.payload, 'bruto_g');
			silo.ultimo_fin_en = campo(m.payload, 'fin_en');
			silo.ultimo_parcial_mutacion_id = m.mutacion_id ?? null;
			silo.turnos += 1;
		}
	}
	return [...abiertos.values()].sort((a, b) => a.abierto_en.localeCompare(b.abierto_en));
}

/** El bruto contra el que se compara la entrega: el último pesado, o el silo vacío. */
export function anteriorDe(silo: Pick<SiloAbierto, 'ultimo_bruto_g' | 'tara_base_g'>): number {
	return silo.ultimo_bruto_g ?? silo.tara_base_g;
}

/** Desde cuándo corre el turno que se entrega: el fin del anterior, o la apertura. */
export function desdeDe(silo: Pick<SiloAbierto, 'ultimo_fin_en' | 'abierto_en'>): string {
	return silo.ultimo_fin_en ?? silo.abierto_en;
}

// ── Lo que el teléfono sabe de los silos ya cerrados ──────────────────────

/** Dónde y desde cuándo estuvo un silo: lo lleva el resumen de su apertura y de su cierre. */
export interface IntervaloEnResumen {
	silo_fisico_id: string;
	trieur_id: string;
	abierto_en: string;
	/**
	 * El lote del silo (20/10, §12.20, ronda de disensor sobre el plan): el recibo del cierre pisa al de la apertura,
	 * y sin el lote «Producción, últimas 24 horas» no sabía si una pesada propia de ese silo suma. Lo encolado antes
	 * no lo trae, y entonces la pesada no suma.
	 */
	lote_campo_id?: string;
}

/** Un silo con su intervalo: `cerrado_en` null mientras siga abierto. */
export interface IntervaloDeSilo {
	llenado_id: string;
	silo_fisico_id: string;
	trieur_id: string;
	precinto: string;
	abierto_en: string;
	cerrado_en: string | null;
}

/** Un registro de producción del teléfono, recibo o pendiente, con el resumen que lo acompaña. */
export interface RegistroDeProduccion {
	tipo?: string;
	payload?: unknown;
	resumen?: { entidad: string; precinto?: string; intervalo?: IntervaloEnResumen };
}

/**
 * Los silos que este teléfono cerró, sacados de sus recibos (30 días) y de la cola. Es memoria
 * local: no ve lo que cerró otro teléfono ni lo que la oficina corrigió, así que sirve para avisar
 * y no para bloquear (2/10). El recibo es uno por silo y el del cierre pisa al de la apertura, así
 * que el intervalo sale del resumen del cierre, que lo trae congelado de la tarjeta del silo —
 * también del que abrió otro teléfono—; la apertura de la misma historia es el respaldo de lo
 * encolado antes de eso (Codex, ronda del diff, 2/10). Un silo que la proyección sigue viendo
 * abierto no está cerrado, diga lo que diga un recibo viejo.
 */
export function silosCerradosLocales(historia: RegistroDeProduccion[], abiertos: Pick<SiloAbierto, 'llenado_id'>[]): IntervaloDeSilo[] {
	const aperturas = new Map<string, IntervaloDeSilo>();
	for (const m of historia) {
		const llenadoId = campo(m.payload, 'llenado_id');
		if (llenadoId === null) continue;
		if (m.tipo === 'llenado.abrir') {
			aperturas.set(llenadoId, {
				llenado_id: llenadoId,
				silo_fisico_id: campo(m.payload, 'silo_fisico_id') ?? '',
				trieur_id: campo(m.payload, 'trieur_id') ?? '',
				precinto: campo(m.payload, 'precinto') ?? '',
				abierto_en: campo(m.payload, 'abierto_en') ?? '',
				cerrado_en: null,
			});
		} else if (m.tipo === 'llenado.cerrar' || m.tipo === 'llenado.descartar') {
			const cerradoEn = campo(m.payload, 'cerrado_en');
			if (cerradoEn === null) continue;
			const intervalo = m.resumen?.intervalo;
			const conocido = intervalo
				? { llenado_id: llenadoId, ...intervalo, precinto: m.resumen?.precinto ?? '—', cerrado_en: null }
				: aperturas.get(llenadoId);
			if (conocido) aperturas.set(llenadoId, { ...conocido, cerrado_en: cerradoEn });
		}
	}
	const siguenAbiertos = new Set(abiertos.map((s) => s.llenado_id));
	return [...aperturas.values()].filter((s) => s.cerrado_en !== null && !siguenAbiertos.has(s.llenado_id));
}

/**
 * Las dos preguntas que el servidor le hace a una apertura (§5.2.9), en su orden: ¿el silo físico
 * tiene otro llenado que no cerró antes de esa hora? (`silo_ocupado`) y ¿el trieur tenía otro silo
 * abierto en ese instante? (`trieur_ocupado`). Devuelve el silo con el que choca, o null.
 */
export function chocaLaApertura(
	silos: IntervaloDeSilo[],
	apertura: { silo_fisico_id: string | null; trieur_id: string | null; abierto_en: string },
): { motivo: 'silo_ocupado' | 'trieur_ocupado'; con: IntervaloDeSilo } | null {
	const t = apertura.abierto_en;
	const delSilo = silos.find((s) => s.silo_fisico_id === apertura.silo_fisico_id && (s.cerrado_en === null || s.cerrado_en > t));
	if (delSilo) return { motivo: 'silo_ocupado', con: delSilo };
	const delTrieur = silos.find((s) => s.trieur_id === apertura.trieur_id && s.abierto_en <= t && (s.cerrado_en === null || s.cerrado_en > t));
	if (delTrieur) return { motivo: 'trieur_ocupado', con: delTrieur };
	return null;
}

// ── Las mezclas de blendeo al abrir un silo (docs/esquema.md §12.16) ──────

/**
 * Cuántas jornadas sigue arriba en «¿De qué lote es?» una mezcla terminada: los silos que salen tarde con
 * lo que quedó en la línea la siguen eligiendo. `[ABIERTO]` en §12.16: tres, a confirmar con Agata.
 */
export const JORNADAS_DE_MEZCLA_TERMINADA = 3;

/** Un lote del catálogo, como lo trae `lotes_campo` (un batch de blendeo vive ahí con su tipo). */
export interface LoteDelCatalogo {
	id: string;
	nombre: string;
	activo: number;
	tipo?: 'campo' | 'blendeo';
	campania_id?: string | null;
	cerrado_en?: string | null;
}

const DIA_MS = 24 * 60 * 60 * 1000;
/** La jornada de planta (06→30) de un instante del servidor (ISO con zona): la fecha local menos seis horas. */
const jornadaDe = (ms: number): number => Date.parse(`${instanteLocal(ms - 6 * 60 * 60 * 1000).slice(0, 10)}T00:00:00Z`);

/**
 * Las mezclas que «¿De qué lote es?» ofrece (§12.16): las activas de la campaña del silo —un blendeo de
 * otra campaña lo rechazaría el servidor (`campania_cruzada`; Codex, 9/10: el teléfono no filtraba)—.
 * `recientes` son las en curso y las terminadas hace menos de `JORNADAS_DE_MEZCLA_TERMINADA` jornadas,
 * contadas desde la hora del servidor del snapshot (nunca desde el reloj del teléfono, que no tiene tope,
 * como el techo de la bajada); `viejas`, las demás terminadas. Sin la hora del snapshot no se puede
 * contar: las terminadas van todas a `viejas` y las en curso siguen arriba.
 */
export function mezclasParaElegir(
	lotes: LoteDelCatalogo[],
	campaniaId: string | null,
	horaDelCatalogo: string | undefined,
): { recientes: LoteDelCatalogo[]; viejas: LoteDelCatalogo[] } {
	const deLaCampania = lotes.filter((l) => l.tipo === 'blendeo' && l.activo === 1 && campaniaId !== null && l.campania_id === campaniaId);
	const horaMs = horaDelCatalogo === undefined ? Number.NaN : Date.parse(horaDelCatalogo);
	const reciente = (l: LoteDelCatalogo): boolean => {
		if (l.cerrado_en === null || l.cerrado_en === undefined) return true;
		const cierreMs = Date.parse(l.cerrado_en);
		if (!Number.isFinite(horaMs) || !Number.isFinite(cierreMs)) return false;
		return (jornadaDe(horaMs) - jornadaDe(cierreMs)) / DIA_MS < JORNADAS_DE_MEZCLA_TERMINADA;
	};
	// Las en curso primero, después las terminadas más nuevas; dentro de cada grupo, la de número más alto.
	const orden = (a: LoteDelCatalogo, b: LoteDelCatalogo): number =>
		Number(a.cerrado_en != null) - Number(b.cerrado_en != null) || (b.cerrado_en ?? '').localeCompare(a.cerrado_en ?? '') || b.nombre.localeCompare(a.nombre, 'es', { numeric: true });
	return { recientes: deLaCampania.filter(reciente).sort(orden), viejas: deLaCampania.filter((l) => !reciente(l)).sort(orden) };
}

/**
 * El catálogo es de una jornada anterior a la de ahora, o no se sabe de cuándo es: un catálogo viejo sin
 * señal no sabe de una mezcla empezada después, así que con él tampoco se marca un lote solo (§12.16).
 */
export function catalogoDeOtraJornada(horaDelCatalogo: string | undefined, ahoraMs: number): boolean {
	const horaMs = horaDelCatalogo === undefined ? Number.NaN : Date.parse(horaDelCatalogo);
	return !Number.isFinite(horaMs) || jornadaDe(horaMs) < jornadaDe(ahoraMs);
}
